import axios from 'axios';
import {resolveEmailSha256, resolveRecoUid} from './RecommendationsService';

const CHAT_URL =
    (typeof process !== 'undefined' && process.env && process.env.CHAT_URL)
    || (typeof window !== 'undefined' && window.__VOICE_SEARCH_ENV_SETTINGS__?.CHAT_URL)
    || 'https://pixel.recomdo.ai/api/conversation/chat';

const LATEST_SESSION_URL =
    (typeof process !== 'undefined' && process.env && process.env.LATEST_CONVERSATION_URL)
    || (typeof window !== 'undefined' && window.__VOICE_SEARCH_ENV_SETTINGS__?.LATEST_CONVERSATION_URL)
    || 'https://pixel.recomdo.ai/api/conversation/sessions/latest';

const CHAT_TIMEOUT_MS = Number(
    (typeof process !== 'undefined' && process.env && process.env.CHAT_TIMEOUT_MS)
    || (typeof window !== 'undefined' && window.__VOICE_SEARCH_ENV_SETTINGS__?.CHAT_TIMEOUT_MS)
    || 12000
);

const EMPTY_CHAT_RESPONSE = {
    reply: '',
    intent: null,
    preferences: {},
    semantic_sentence: null,
    timestamp: null,
    person_id: null,
};

function resolveMerchant() {
    try {
        const cfgMerchant = window.__VOICE_SEARCH_CONFIG__?.merchant;
        if (cfgMerchant) return cfgMerchant;
        if (window.location?.hostname) return window.location.hostname;
    } catch (_) {
    }
    return 'unknown';
}

function normalizeChatResponse(payload) {
    const root = payload && typeof payload === 'object' ? payload : {};
    const nested = root.data && typeof root.data === 'object' ? root.data : {};
    const merged = {...root, ...nested};

    const reply = [merged.reply, merged.text, merged.message, merged.response]
        .find((v) => typeof v === 'string' && v.trim()) || '';

    return {
        ...EMPTY_CHAT_RESPONSE,
        ...merged,
        reply,
    };
}

/**
 * Calls the conversation chat endpoint after user speech is finalized.
 */
export async function chatConversation({message, merchant = null, clientKey = null, emailSha256 = null, recoUid = null}) {
    try {
        const safeMessage = typeof message === 'string' ? message.trim() : '';
        if (!safeMessage) {
            return EMPTY_CHAT_RESPONSE;
        }

        const payload = {
            email_sha256: emailSha256 ?? resolveEmailSha256(),
            reco_uid: recoUid ?? resolveRecoUid(),
            merchant: merchant || resolveMerchant(),
            client_key: clientKey || window.__VOICE_SEARCH_ENV_SETTINGS__?.REACT_APP_CLIENT_KEY || null,
            message: safeMessage,
        };

        const response = await axios.post(
            CHAT_URL,
            payload,
            {headers: {'accept': 'application/json', 'Content-Type': 'application/json'}, timeout: CHAT_TIMEOUT_MS}
        );

        if (response?.status >= 200 && response?.status < 300 && response?.data) {
            return normalizeChatResponse(response.data);
        }

        return EMPTY_CHAT_RESPONSE;
    } catch (error) {
        if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
            const isTimeout = error?.code === 'ECONNABORTED';
            const isCanceled = error?.code === 'ERR_CANCELED' || axios.isCancel?.(error);
            console.warn('chatConversation() failed:', isTimeout ? 'timeout' : (isCanceled ? 'canceled' : (error?.message || error)));
        }
        return {reply: 'Sorry, I could not process that request right now.', intent: 'request_failed'};
    }
}

/**
 * Fetches the most recent conversation session for the current shopper.
 */
export async function getLatestConversationSession({merchant = null, clientKey = null, emailSha256 = null, recoUid = null} = {}) {
    try {
        const params = {
            email_sha256: emailSha256 ?? resolveEmailSha256(),
            reco_uid: recoUid ?? resolveRecoUid(),
            merchant: merchant || resolveMerchant(),
            client_key: clientKey || window.__VOICE_SEARCH_ENV_SETTINGS__?.REACT_APP_CLIENT_KEY || null,
        };

        const response = await axios.get(
            LATEST_SESSION_URL,
            {
                params,
                headers: {accept: 'application/json'},
                timeout: 5000,
            }
        );

        if (response?.status >= 200 && response?.status < 300 && response?.data) {
            return response.data;
        }

        return {count: 0, sessions: []};
    } catch (error) {
        if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
            console.warn('getLatestConversationSession() failed:', error?.message || error);
        }
        return {count: 0, sessions: []};
    }
}

